/**
 * Runtime contract for the guided-flow option of the operational archetype:
 * the chrome variant, the progress copy, the move back, and the choice summary
 * whose every item points at a declared stage. A key only the compact strip
 * renders (the lead figure, the phone disclosure) is refused on the standard
 * chrome, and the masthead photo, which only the standard header draws, is
 * refused on the compact chrome, rather than accepted and dropped.
 */
import {
  exactOperationalKeys as exact,
  failOperationalDescriptor as fail,
  operationalArray as array,
  operationalObject as object,
  operationalText as text,
} from './FmOperationalWorkspaceValidation.shared';
import { validateElement } from './elements/elements.validation';

/** The outcome beside the choices is a list of facts or a table, nothing else. */
const SUMMARY_OUTCOME_ELEMENTS = new Set(['facts', 'table']);

const GUIDANCE_CHROMES = new Set(['standard', 'compact']);
const SUMMARY_KEYS = ['title', 'changeLabel', 'items', 'outcome', 'readOnly'];
const COMPACT_SUMMARY_KEYS = [...SUMMARY_KEYS, 'lead', 'expandLabel', 'collapseLabel'];

function validateChoice(value: unknown, path: string, stageIds: ReadonlySet<string>, keys: string[]): void {
  const choice = object(value, path);
  exact(choice, new Set(keys), path);
  if (!stageIds.has(text(choice.stageId, `${path}.stageId`))) {
    fail(`${path}.stageId`, 'must reference a declared stage');
  }
  text(choice.label, `${path}.label`);
  text(choice.value, `${path}.value`);
  ['unit', 'caption'].forEach((key) => {
    if (choice[key] !== undefined) text(choice[key], `${path}.${key}`);
  });
}

export function validateGuidance(
  value: unknown,
  stagesValue: unknown,
  mastheadValue: unknown,
): void {
  if (value === undefined) return;
  const guidance = object(value, 'descriptor.guidance');
  exact(
    guidance,
    new Set(['chrome', 'progressLabel', 'previousLabel', 'summary']),
    'descriptor.guidance',
  );
  if (guidance.chrome !== undefined && !GUIDANCE_CHROMES.has(String(guidance.chrome))) {
    fail('descriptor.guidance.chrome', "must be 'standard' or 'compact'");
  }
  const compact = guidance.chrome === 'compact';
  if (compact && mastheadValue !== undefined) {
    fail(
      'descriptor.masthead',
      "is not drawn by the compact guided chrome; drop it or use guidance.chrome 'standard'",
    );
  }
  const progress = text(guidance.progressLabel, 'descriptor.guidance.progressLabel');
  if (!progress.includes('{index}') || !progress.includes('{count}')) {
    fail('descriptor.guidance.progressLabel', 'must carry the {index} and {count} placeholders');
  }
  text(guidance.previousLabel, 'descriptor.guidance.previousLabel');
  const stages = array(stagesValue, 'descriptor.stages').map((item, index) =>
    object(item, `descriptor.stages[${index}]`),
  );
  if (stages.length < 2) fail('descriptor.guidance', 'needs at least two stages to guide through');
  if (guidance.summary === undefined) return;
  validateSummary(guidance.summary, compact, stages);
}

function validateSummary(
  value: unknown,
  compact: boolean,
  stages: readonly Record<string, unknown>[],
): void {
  const summary = object(value, 'descriptor.guidance.summary');
  exact(
    summary,
    new Set(compact ? COMPACT_SUMMARY_KEYS : SUMMARY_KEYS),
    'descriptor.guidance.summary',
  );
  text(summary.title, 'descriptor.guidance.summary.title');
  text(summary.changeLabel, 'descriptor.guidance.summary.changeLabel');
  if (compact) {
    text(summary.expandLabel, 'descriptor.guidance.summary.expandLabel');
    text(summary.collapseLabel, 'descriptor.guidance.summary.collapseLabel');
  }
  if (summary.readOnly !== undefined && typeof summary.readOnly !== 'boolean') {
    fail('descriptor.guidance.summary.readOnly', 'must be a boolean when set');
  }
  const stageIds = new Set(stages.map((stage) => String(stage.id)));
  const items = array(summary.items, 'descriptor.guidance.summary.items');
  if (!items.length) fail('descriptor.guidance.summary.items', 'must name at least one choice');
  items.forEach((item, index) =>
    validateChoice(item, `descriptor.guidance.summary.items[${index}]`, stageIds, [
      'stageId',
      'label',
      'value',
    ]),
  );
  if (summary.lead !== undefined) {
    validateChoice(summary.lead, 'descriptor.guidance.summary.lead', stageIds, [
      'stageId',
      'label',
      'value',
      'unit',
      'caption',
    ]);
  }
  if (summary.outcome === undefined) return;
  const outcome = object(summary.outcome, 'descriptor.guidance.summary.outcome');
  if (!SUMMARY_OUTCOME_ELEMENTS.has(String(outcome.element))) {
    fail('descriptor.guidance.summary.outcome', 'must be a facts or table element');
  }
  validateElement(outcome, 'descriptor.guidance.summary.outcome');
}
